import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, User, Plus, X } from 'lucide-react';
import type { Client } from '../types';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Appelé quand l'utilisateur sélectionne un client existant (pour pré-remplir tel/email/lieu) */
  onPickClient?: (c: Client) => void;
  clients: Client[];
  placeholder?: string;
}

const inputCls =
  'w-full pl-9 pr-9 py-2.5 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500/50';

/**
 * Champ "Client" avec recherche dans la base existante.
 * - Tape pour filtrer parmi les clients enregistrés (nom, société, email, tel, ville)
 * - Sélectionne pour auto-remplir les champs liés
 * - Sinon, la saisie libre est conservée (création implicite)
 */
export default function ClientPicker({ value, onChange, onPickClient, clients, placeholder = 'Rechercher ou saisir un client…' }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatLabel = (c: Client) => {
    const nameParts = [c.prenom, c.nom].filter(Boolean).join(' ').trim();
    return c.societe ? (nameParts ? `${c.societe} — ${nameParts}` : c.societe) : (nameParts || c.email || '—');
  };

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    // Au repos : 2 derniers clients (les plus récents en tête de liste)
    if (!q) return clients.slice(0, 2);
    // Pendant la saisie : filtre dans toute la base dès la 1ère lettre
    return clients
      .filter((c) => {
        const hay = [c.nom, c.prenom, c.societe, c.email, c.tel, c.ville]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 12);
  }, [value, clients]);

  const exactMatch = useMemo(
    () => clients.find((c) => formatLabel(c).toLowerCase() === value.trim().toLowerCase()),
    [clients, value]
  );

  const handlePick = (c: Client) => {
    onChange(formatLabel(c));
    onPickClient?.(c);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={inputCls}
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
          title="Effacer"
        >
          <X size={13} strokeWidth={1.5} />
        </button>
      )}

      {open && (suggestions.length > 0 || value.trim()) && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {suggestions.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-slate-500 italic flex items-center gap-2">
              <Plus size={12} strokeWidth={1.5} className="text-amber-400" />
              Aucun client trouvé — "{value}" sera créé comme nouveau contact.
            </div>
          ) : (
            <>
              {!exactMatch && value.trim() && (
                <div className="px-3 py-2 text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-900/40">
                  Suggestions ({suggestions.length})
                </div>
              )}
              {suggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handlePick(c)}
                  className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-amber-500/10 hover:text-amber-300 border-b border-slate-800/50 last:border-0 flex items-start gap-2"
                >
                  <User size={12} className="text-amber-400 mt-1 shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{formatLabel(c)}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {[c.email, c.tel, c.ville].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
